import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, Empty } from "@/components/Shared";
import { ChatThread } from "@/components/ChatThread";
import { QuickReplies } from "@/components/QuickReplies";
import { AttachMenu } from "@/components/AttachMenu";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { timeAgo } from "@/lib/format";
import { MessageSquare, Search, Send, ArrowLeft, ClipboardPlus, X, LayoutTemplate, ExternalLink, Clock, UserCircle, StickyNote, Eye, UserPlus, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const initials = (n) => (n || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const fmtWait = (m) => { if (m == null) return ""; const h = Math.floor(m / 60), mm = m % 60; return h ? `${h}h ${mm}m` : `${mm}m`; };
const SLA_CLS = { "On Track": "bg-[#DCFCE7] text-[#15803D]", "Due Soon": "bg-[#FEF3C7] text-[#92400E]", "Breached": "bg-[#FEE2E2] text-[#B91C1C]" };
function SlaBadge({ status, minutes, compact }) {
  if (!status) return null;
  return <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"} ${SLA_CLS[status] || ""}`} data-testid="sla-badge" title={`Awaiting reply · ${status}`}><Clock className="w-3 h-3" />{fmtWait(minutes)}</span>;
}

function OwnerPicker({ conv, users, onChanged, compact = false }) {
  const set = async (v) => {
    try { const { data } = await api.post(`/conversations/${conv.id}/owner`, { user_id: v === "none" ? null : v }); toast.success(data.owner_name ? `Owner: ${data.owner_name}` : "Owner cleared"); onChanged(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Select value={conv.owner_id || "none"} onValueChange={set}>
      <SelectTrigger className={`${compact ? "h-8 w-40 text-xs" : "h-9 w-48"} bg-white`} data-testid="thread-owner"><UserCircle className="w-4 h-4 text-[#6B7189] mr-1" /><SelectValue placeholder="Owner" /></SelectTrigger>
      <SelectContent><SelectItem value="none">No owner</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
    </Select>);
}

function ThreadList({ items, activeId, onPick, q, setQ, view, setView, meId }) {
  const VIEWS = [["all", "All"], ["mine", "Mine"], ["unowned", "Unassigned"], ["unread", "Unread"], ["awaiting", "Awaiting"]];
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-[#E6E8F0] space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
          <Input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone…" className="pl-9 h-10" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-3 px-3" data-testid="inbox-views">
          {VIEWS.map(([id, label]) => <button key={id} onClick={() => setView(id)} data-testid={`inbox-view-${id}`}
            className={`h-9 md:h-8 px-3.5 rounded-full text-xs font-medium border transition-colors shrink-0 whitespace-nowrap ${view === id ? "bg-[#101D51] text-white border-[#101D51]" : "bg-white text-[#6B7189] border-[#E6E8F0] hover:border-[#C7CBDA]"}`}>{label}</button>)}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 && <Empty icon={MessageSquare} title={view === "mine" ? "Nothing assigned to you" : "No conversations"} hint={view === "mine" ? "Set yourself as owner on a chat to see it here." : "Inbound WhatsApp messages will appear here."} />}
        {items.map((c) => (
          <button key={c.id} onClick={() => onPick(c.id)} data-testid={`thread-${c.phone}`}
            className={`w-full text-left px-3 py-3 flex gap-3 border-b border-[#F0F1F5] hover:bg-[#F5F6FA] transition-colors ${activeId === c.id ? "bg-[#F5F6FA]" : ""}`}>
            <div className="w-10 h-10 rounded-full bg-[#101D51]/10 text-[#101D51] font-semibold flex items-center justify-center shrink-0">{c.display_name?.[0]}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-[#101D51] truncate">{c.display_name}</span>
                <span className="text-[11px] text-[#6B7189] shrink-0">{timeAgo(c.last_message_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs text-[#6B7189] truncate">{c.last_message_direction === "outbound" ? "You: " : ""}{c.last_message_preview}</span>
                {c.unread_count > 0 && <span data-testid={`unread-${c.phone}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#0B7C5A] text-white text-[11px] font-semibold flex items-center justify-center shrink-0">{c.unread_count}</span>}
              </div>
              <div className="mt-1 flex items-center gap-1 text-[11px]" data-testid={`owner-${c.phone}`}>
                {c.owner_name
                  ? <><span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${c.owner_id === meId ? "bg-[#0B7C5A] text-white" : "bg-[#101D51] text-white"}`}>{initials(c.owner_name)}</span><span className={c.owner_id === meId ? "text-[#0B7C5A] font-medium" : "text-[#6B7189]"}>{c.owner_id === meId ? "You" : c.owner_name}</span></>
                  : <span className="text-[#9AA0B5] italic">Unassigned</span>}
                {c.sla_status && <span className="ml-auto"><SlaBadge status={c.sla_status} minutes={c.waiting_minutes} compact /></span>}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CreateEnquiryDialog({ conv, messages, selected, open, onClose, onCreated, users }) {
  const { user } = useAuth();
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("Standard");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setDesc(messages.filter((m) => selected.has(m.id) && m.body).map((m) => m.body).join("\n")); setOwner(conv?.owner_id || user?.id || ""); }
  }, [open, selected, messages, conv, user]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/enquiries", { customer_id: conv.customer_id, description: desc, source: "WhatsApp",
        priority, conversation_id: conv.id, message_ids: [...selected], assigned_to: owner || null });
      toast.success(`${data.number} created${data.assigned_name ? ` · assigned to ${data.assigned_name}` : ""}`); onCreated(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New enquiry from {conv?.display_name}</DialogTitle></DialogHeader>
        <div className="text-xs text-[#6B7189]">{selected.size} message(s) selected · media attachments carry over</div>
        <Textarea data-testid="enquiry-desc" rows={5} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Requirement…" />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-[#6B7189]">Priority</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="enquiry-priority" className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["Standard", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs font-medium text-[#6B7189]">Assign to (owner)</label>
            <Select value={owner || "none"} onValueChange={(v) => setOwner(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="enquiry-owner" className="h-10 mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Leave unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.id === user?.id ? " (me)" : ""}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {owner && !conv?.owner_id && <div className="text-[11px] text-[#6B7189]">This customer has no owner yet — the assignee will also become the customer owner.</div>}
        <DialogFooter>
          <Button onClick={save} disabled={saving || !desc.trim()} className="bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="enquiry-create-submit">Create enquiry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Composer({ conv, onSent }) {
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/whatsapp/templates").then((r) => setTemplates(r.data)); }, []);
  const send = async (payload) => {
    setSending(true);
    try { await api.post(`/conversations/${conv.id}/messages`, payload); setText(""); setTpl(""); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="border-t border-[#E6E8F0] bg-white p-2 sm:p-3 pb-safe shrink-0 sticky bottom-0" data-testid="composer">
      {!conv.window_open && (
        <div className="text-xs text-[#B45309] bg-[#FEF3C7] rounded-lg px-3 py-2 mb-2 flex items-center gap-2" data-testid="window-closed-banner">
          <Clock className="w-3.5 h-3.5" /> 24-hour window closed — only approved templates can be sent.
        </div>
      )}
      <div className="flex gap-2 items-end">
        <QuickReplies onPick={(body) => setText((t) => (t ? `${t}\n${body}` : body))} />
        <AttachMenu conv={conv} disabled={!conv.window_open} onSent={onSent} testid="composer-attach" />
        <Select value={tpl} onValueChange={(v) => { setTpl(v); send({ template_name: v }); }}>
          <SelectTrigger data-testid="template-picker" className="w-11 h-11 md:w-10 md:h-10 px-0 justify-center shrink-0 [&>svg:last-child]:hidden" title="Send template">
            <LayoutTemplate className="w-4 h-4 text-[#101D51]" />
          </SelectTrigger>
          <SelectContent>
            {templates.length === 0 && <div className="px-3 py-2 text-xs text-[#6B7189]">No templates yet</div>}
            {templates.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} <span className="text-[#6B7189] text-xs">· {t.meta_status}</span></SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea data-testid="composer-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); text.trim() && send({ body: text }); } }}
          placeholder={conv.window_open ? "Type a reply…" : "Window closed — pick a template"} disabled={!conv.window_open}
          className="min-h-[44px] md:min-h-[40px] max-h-32 resize-none text-base md:text-sm" />
        <Button onClick={() => send({ body: text })} disabled={sending || !text.trim() || !conv.window_open}
          className="h-11 w-11 md:h-10 md:w-10 p-0 bg-[#0B7C5A] hover:bg-[#09664a] shrink-0" data-testid="composer-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function ThreadPane({ convId, onBack, refreshList, users }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [dlg, setDlg] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    const [c, m] = await Promise.all([api.get(`/conversations/${convId}`), api.get(`/conversations/${convId}/messages`)]);
    setConv(c.data); setMessages(m.data);
  }, [convId]);
  const loadNotes = useCallback(() => api.get(`/conversations/${convId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([])), [convId]);
  useEffect(() => {
    setSelectMode(false); setSelected(new Set()); setNotesOpen(false); setNotes(null);
    load().then(() => api.post(`/conversations/${convId}/read`).then(refreshList));
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [convId, load, refreshList]);
  useEffect(() => {
    const beat = () => { if (!document.hidden) api.post(`/conversations/${convId}/presence`).then((r) => setViewers(r.data.viewers || [])).catch(() => {}); };
    beat(); const t = setInterval(beat, 20000);
    return () => { setViewers([]); clearInterval(t); };
  }, [convId]);
  useEffect(() => { if (notesOpen && notes === null) loadNotes(); }, [notesOpen, notes, loadNotes]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const takeover = async (v) => {
    const { data } = await api.post(`/conversations/${convId}/takeover`, { agent_takeover: v });
    setConv((c) => ({ ...c, ...data })); toast.success(v ? "Agent has taken over — auto-replies paused" : "Auto-replies resumed");
  };
  const assignToMe = async () => {
    try { const { data } = await api.post(`/conversations/${convId}/owner`, { user_id: user?.id }); setConv((c) => ({ ...c, ...data })); refreshList(); toast.success("Assigned to you"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try { const { data } = await api.post(`/conversations/${convId}/notes`, { body: noteText }); setNotes((n) => [...(n || []), data]); setNoteText(""); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingNote(false); }
  };

  if (!conv || !messages) return <Loading />;
  return (
    <div className="flex flex-col h-full">
      <div className="h-14 px-2 md:px-3 flex items-center gap-2 md:gap-3 border-b border-[#E6E8F0] bg-white shrink-0">
        <button onClick={onBack} className="md:hidden text-[#101D51] w-10 h-10 -ml-1 flex items-center justify-center shrink-0" data-testid="thread-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="w-9 h-9 rounded-full bg-[#101D51]/10 text-[#101D51] font-semibold flex items-center justify-center">{conv.display_name?.[0]}</div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#101D51] truncate flex items-center gap-2">{conv.display_name}
            <button onClick={() => navigate(`/customers/${conv.customer_id}`)} className="text-[#6B7189] hover:text-[#101D51]" data-testid="open-customer"><ExternalLink className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-xs text-[#6B7189] truncate flex items-center gap-2">+91 {conv.phone} · {conv.window_open ? <span className="text-[#0B7C5A]">window open</span> : "window closed"}
            {conv.sla_status && <SlaBadge status={conv.sla_status} minutes={conv.waiting_minutes} compact />}
            {conv.enquiries?.length > 0 && <> · {conv.enquiries.map((e) => <button key={e.id} onClick={() => navigate(`/enquiries/${e.id}`)} className="underline ml-1" data-testid={`thread-enq-${e.number}`}>{e.number}</button>)}</>}
          </div>
        </div>
        <div className="hidden sm:block"><OwnerPicker conv={conv} users={users} compact onChanged={(d) => { setConv((c) => ({ ...c, ...d })); refreshList(); }} /></div>
        {conv.owner_id !== user?.id && <Button size="sm" variant="outline" onClick={assignToMe} className="h-9 hidden sm:flex" data-testid="assign-to-me"><UserPlus className="w-4 h-4" /> <span className="hidden lg:inline ml-1">Assign to me</span></Button>}
        <Button size="sm" variant={notesOpen ? "default" : "outline"} onClick={() => setNotesOpen((v) => !v)} className={`h-9 ${notesOpen ? "bg-[#101D51]" : ""}`} data-testid="notes-toggle"><StickyNote className="w-4 h-4" /><span className="hidden lg:inline ml-1">Notes</span>{notes?.length ? <span className="ml-1 text-[10px]">({notes.length})</span> : null}</Button>
        <label className="hidden sm:flex items-center gap-2 text-xs text-[#6B7189]" title="Pause auto-replies while an agent handles this chat">
          Agent <Switch checked={!!conv.agent_takeover} onCheckedChange={takeover} data-testid="takeover-toggle" />
        </label>
        {selectMode ? (
          <>
            <Button size="sm" onClick={() => setDlg(true)} disabled={selected.size === 0} className="h-9 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="create-enquiry-confirm">
              Create ({selected.size})
            </Button>
            <button onClick={() => { setSelectMode(false); setSelected(new Set()); }} className="text-[#6B7189]" data-testid="select-cancel"><X className="w-5 h-5" /></button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setSelectMode(true)} className="h-9" data-testid="create-enquiry-btn">
            <ClipboardPlus className="w-4 h-4" /> <span className="hidden sm:inline ml-1">Create enquiry</span>
          </Button>
        )}
      </div>
      <div className="sm:hidden flex items-center gap-2 px-3 py-2 border-b border-[#F0F1F5] bg-[#FAFBFD]" data-testid="thread-mobile-tools">
        <OwnerPicker conv={conv} users={users} compact onChanged={(d) => { setConv((c) => ({ ...c, ...d })); refreshList(); }} />
        <label className="ml-auto flex items-center gap-2 text-xs text-[#6B7189]">Agent <Switch checked={!!conv.agent_takeover} onCheckedChange={takeover} data-testid="takeover-toggle-mobile" /></label>
      </div>
      {selectMode && <div className="bg-[#0B7C5A]/10 text-[#0B7C5A] text-xs px-4 py-2" data-testid="select-mode-hint">Tap customer messages to include them in the enquiry.</div>}
      {viewers.length > 0 && <div className="bg-[#FEF3C7] text-[#92400E] text-xs px-4 py-1.5 flex items-center gap-2" data-testid="collision-banner"><Eye className="w-3.5 h-3.5" /> {viewers.map((v) => v.name).join(", ")} {viewers.length === 1 ? "is" : "are"} also viewing this chat</div>}
      {notesOpen && (
        <div className="border-b border-[#E6E8F0] bg-[#FFFDF5] max-h-56 flex flex-col" data-testid="notes-panel">
          <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#92400E] flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Internal notes · only your team sees these</div>
          <div className="flex-1 overflow-y-auto px-4 py-1 space-y-2">
            {notes === null ? <div className="py-3 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-[#6B7189]" /></div>
              : notes.length === 0 ? <div className="text-xs text-[#6B7189] py-3 text-center">No notes yet. Add context for your team.</div>
                : notes.map((n) => (
                  <div key={n.id} className="text-sm" data-testid={`note-${n.id}`}>
                    <div className="text-[#101D51] whitespace-pre-wrap">{n.body}</div>
                    <div className="text-[10px] text-[#9AA0B5]">{n.by_name || "Someone"} · {timeAgo(n.at)}</div>
                  </div>))}
          </div>
          <div className="flex items-end gap-2 p-2 border-t border-[#F0E8C8]">
            <Textarea rows={1} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note…" data-testid="note-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(); } }} className="min-h-[38px] max-h-24 resize-none text-sm bg-white" />
            <Button size="sm" onClick={addNote} disabled={savingNote || !noteText.trim()} className="h-9 bg-[#101D51] hover:bg-[#080852] shrink-0" data-testid="note-save">{savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button>
          </div>
        </div>)}
      <div className="flex-1 overflow-y-auto"><ChatThread messages={messages} selectable={selectMode} selected={selected} onToggle={toggle} className="min-h-full"
        onRetry={async (mid) => { try { const { data } = await api.post(`/whatsapp/messages/${mid}/retry`); toast[data.sent ? "success" : "error"](data.sent ? "Sent" : data.message.error || "Still failing"); load(); } catch (e) { toast.error(apiError(e)); } }} /></div>
      <Composer conv={conv} onSent={() => { load(); refreshList(); }} />
      <CreateEnquiryDialog conv={conv} messages={messages} selected={selected} open={dlg} onClose={() => setDlg(false)} users={users}
        onCreated={(e) => { setDlg(false); setSelectMode(false); setSelected(new Set()); navigate(`/enquiries/${e.id}`); }} />
    </div>
  );
}

export default function Inbox() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const active = params.get("c");
  const [items, setItems] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [view, setViewState] = useState(() => localStorage.getItem("tradedesk.inbox.view") || "all");
  const setView = (v) => { setViewState(v); localStorage.setItem("tradedesk.inbox.view", v); };
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false))).catch(() => {}); }, []);
  const refreshList = useCallback(async () => {
    const { data } = await api.get(`/conversations?page_size=100${q ? `&q=${encodeURIComponent(q)}` : ""}${view === "mine" ? "&mine=true" : ""}${view === "unread" ? "&unread=true" : ""}${view === "awaiting" ? "&awaiting=true" : ""}`);
    setItems(view === "unowned" ? data.items.filter((c) => !c.owner_id) : data.items);
  }, [q, view]);
  useEffect(() => { refreshList(); const t = setInterval(() => { if (!document.hidden) refreshList(); }, 12000); return () => clearInterval(t); }, [refreshList]);

  if (!items) return <Loading />;
  return (
    <div className={`${active ? "h-[calc(100dvh-3.5rem)]" : "h-[calc(100dvh-7rem)]"} md:h-[calc(100dvh-12rem)] -m-3 sm:-m-5 md:m-0 bg-white md:rounded-xl md:border md:border-[#E6E8F0] md:shadow-sm overflow-hidden flex`} data-testid="inbox-page">
      <div className={`w-full md:w-80 lg:w-96 shrink-0 border-r border-[#E6E8F0] ${active ? "hidden md:block" : "block"}`}>
        <ThreadList items={items} activeId={active} onPick={(id) => setParams({ c: id })} q={q} setQ={setQ} view={view} setView={setView} meId={user?.id} />
      </div>
      <div className={`flex-1 min-w-0 ${active ? "flex flex-col" : "hidden md:flex"} md:items-center md:justify-center`}>
        {active ? <div className="flex-1 min-h-0 w-full"><ThreadPane convId={active} onBack={() => setParams({})} refreshList={refreshList} users={users} /></div>
          : <Empty icon={MessageSquare} title="Pick a conversation" hint="Reply, take over from the bot, or select messages to create an enquiry." />}
      </div>
    </div>
  );
}
